import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

export interface TourStep {
    /** Value of the data-tour attribute on the element to highlight. */
    target: string;
    titleId: string;
    titleDefault: string;
    bodyId: string;
    bodyDefault: string;
}

interface Props {
    steps: TourStep[];
    open: boolean;
    onClose: () => void;
}

const HALO_PAD = 10;
const TOOLTIP_GAP = 16;
const TOOLTIP_MAX_WIDTH = 360;
const TOOLTIP_SIDE_GUTTER = 16;

/**
 * Hand-rolled product tour. No third-party dep.
 *
 * Mechanics:
 *   - Four backdrop rectangles around the highlighted element (so the dark
 *     overlay catches clicks but the target shows through clean — simpler
 *     and more reliable than clip-path cutouts across browsers).
 *   - A `__halo` div draws a glowing ring on top of the target.
 *   - The tooltip auto-flips above/below depending on viewport room and is
 *     horizontally clamped to stay on screen.
 *
 * Re-measures on resize + scroll + step change. Targets are located by
 * data-tour="..." attributes so the tour is decoupled from class names.
 */
const GuidedTour: React.FC<Props> = ({ steps, open, onClose }) => {
    const intl = useIntl();
    const [stepIndex, setStepIndex] = useState(0);
    const [rect, setRect] = useState<DOMRect | null>(null);
    const [tooltipHeight, setTooltipHeight] = useState(220);
    const tooltipRef = useRef<HTMLDivElement | null>(null);

    // Reset to step 0 every time the tour opens
    useEffect(() => {
        if (open) setStepIndex(0);
    }, [open]);

    // Measure target rect + scroll into view when step changes / on resize / on scroll
    useEffect(() => {
        if (!open) return;
        const step = steps[stepIndex];

        // Tour anchors are `display: contents` (so they don't disturb the
        // canvas grid). Such elements report 0×0 from getBoundingClientRect;
        // we compute the union of their visible children instead and use the
        // first visible child as the scroll target.
        const resolveTarget = (el: Element): { rect: DOMRect; scrollEl: HTMLElement } | null => {
            let r = (el as HTMLElement).getBoundingClientRect();
            let scrollEl = el as HTMLElement;
            if (r.width === 0 && r.height === 0 && el.children.length > 0) {
                const childItems = Array.from(el.children)
                    .map((c) => ({ el: c as HTMLElement, rect: (c as HTMLElement).getBoundingClientRect() }))
                    .filter((x) => x.rect.width > 0 || x.rect.height > 0);
                if (childItems.length === 0) return null;
                scrollEl = childItems[0].el;
                const top = Math.min(...childItems.map((c) => c.rect.top));
                const left = Math.min(...childItems.map((c) => c.rect.left));
                const right = Math.max(...childItems.map((c) => c.rect.right));
                const bottom = Math.max(...childItems.map((c) => c.rect.bottom));
                r = {
                    top, left, right, bottom,
                    x: left, y: top,
                    width: right - left,
                    height: bottom - top,
                    toJSON: () => ({}),
                } as DOMRect;
            }
            return { rect: r, scrollEl };
        };

        const measure = () => {
            const el = document.querySelector(`[data-tour="${step.target}"]`);
            if (!el) {
                setRect(null);
                return;
            }
            const result = resolveTarget(el);
            setRect(result?.rect ?? null);
        };

        // Try to bring the target into view first. We always anchor by the
        // first child + use `block: 'start'` so tall sections (Operations
        // spans hundreds of px on mobile) keep their top edge visible —
        // `block: 'center'` on a section taller than the viewport hides
        // the top inside the tour halo with no way to see it.
        const el = document.querySelector(`[data-tour="${step.target}"]`);
        if (el) {
            const result = resolveTarget(el);
            if (result) {
                const offTop = result.rect.top < 80;
                const offBottom = result.rect.bottom > window.innerHeight - 80;
                if (offTop || offBottom) {
                    result.scrollEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    // Small top offset after scroll so the halo doesn't kiss
                    // the viewport edge — visually nicer when the section
                    // sits a touch below the page chrome.
                    window.setTimeout(() => {
                        window.scrollBy({ top: -24, behavior: 'smooth' });
                    }, 250);
                }
            }
        }

        // Initial + delayed measures (the second covers smooth-scroll settling)
        measure();
        const t1 = window.setTimeout(measure, 200);
        const t2 = window.setTimeout(measure, 500);

        window.addEventListener('resize', measure);
        window.addEventListener('scroll', measure, { passive: true });
        return () => {
            window.clearTimeout(t1);
            window.clearTimeout(t2);
            window.removeEventListener('resize', measure);
            window.removeEventListener('scroll', measure);
        };
    }, [open, stepIndex, steps]);

    // Re-measure the tooltip's own height after each step renders so we can
    // place it correctly (especially when it might collide with bottom edge).
    useLayoutEffect(() => {
        if (tooltipRef.current) {
            setTooltipHeight(tooltipRef.current.offsetHeight);
        }
    }, [stepIndex, rect, open]);

    // Keyboard: ESC closes, arrows navigate
    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onClose();
            } else if (e.key === 'ArrowRight' || e.key === 'Enter') {
                e.preventDefault();
                setStepIndex((i) => {
                    if (i >= steps.length - 1) {
                        onClose();
                        return i;
                    }
                    return i + 1;
                });
            } else if (e.key === 'ArrowLeft') {
                e.preventDefault();
                setStepIndex((i) => Math.max(0, i - 1));
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, onClose, steps.length]);

    if (!open) return null;

    const step = steps[stepIndex];
    const isFirst = stepIndex === 0;
    const isLast = stepIndex === steps.length - 1;

    const goNext = () => (isLast ? onClose() : setStepIndex((i) => i + 1));
    const goPrev = () => !isFirst && setStepIndex((i) => i - 1);

    // Tooltip positioning: try below first, fall back above; clamp horizontally.
    //
    // Horizontal bounds come from `.av2-page`'s bounding rect rather than the
    // viewport. On mobile the app's left navbar lives OUTSIDE `.av2-page`
    // (fixed left column), so clamping to `window.innerWidth` would let the
    // tooltip slide under the navbar and get visually clipped. Using the
    // page's own rect keeps the tooltip inside the visible content column.
    const tooltipPos: React.CSSProperties = (() => {
        const VW = typeof window !== 'undefined' ? window.innerWidth : 1024;
        const VH = typeof window !== 'undefined' ? window.innerHeight : 768;
        const pageEl = typeof document !== 'undefined'
            ? (document.querySelector('.av2-page') as HTMLElement | null)
            : null;
        const pageRect = pageEl?.getBoundingClientRect();
        const minLeft = pageRect ? pageRect.left + TOOLTIP_SIDE_GUTTER : TOOLTIP_SIDE_GUTTER;
        const maxRight = pageRect ? pageRect.right - TOOLTIP_SIDE_GUTTER : VW - TOOLTIP_SIDE_GUTTER;
        const availableWidth = Math.max(220, maxRight - minLeft);
        const width = Math.min(TOOLTIP_MAX_WIDTH, availableWidth);

        if (!rect) {
            return {
                top: VH / 2 - tooltipHeight / 2,
                left: minLeft + (availableWidth - width) / 2,
                width,
            };
        }
        let top: number;
        if (rect.bottom + TOOLTIP_GAP + tooltipHeight + 16 <= VH) {
            top = rect.bottom + TOOLTIP_GAP;
        } else if (rect.top - TOOLTIP_GAP - tooltipHeight - 16 >= 0) {
            top = rect.top - TOOLTIP_GAP - tooltipHeight;
        } else {
            // Section is taller than viewport — anchor tooltip near the top
            // so the user sees both halo edge + instructions.
            top = Math.max(16, Math.min(rect.top + TOOLTIP_GAP, VH - tooltipHeight - 16));
        }
        let left = rect.left + rect.width / 2 - width / 2;
        left = Math.max(minLeft, Math.min(left, maxRight - width));
        return { top, left, width };
    })();

    return (
        <div className="av2-tour" role="dialog" aria-modal="true" aria-labelledby="av2-tour-title">
            {/* Four-piece backdrop. Click any piece → close. */}
            {rect ? (
                <>
                    <div
                        className="av2-tour__bk"
                        style={{ top: 0, left: 0, right: 0, height: Math.max(0, rect.top - HALO_PAD) }}
                        onClick={onClose}
                    />
                    <div
                        className="av2-tour__bk"
                        style={{
                            top: rect.top - HALO_PAD,
                            left: 0,
                            width: Math.max(0, rect.left - HALO_PAD),
                            height: rect.height + HALO_PAD * 2,
                        }}
                        onClick={onClose}
                    />
                    <div
                        className="av2-tour__bk"
                        style={{
                            top: rect.top - HALO_PAD,
                            left: rect.right + HALO_PAD,
                            right: 0,
                            height: rect.height + HALO_PAD * 2,
                        }}
                        onClick={onClose}
                    />
                    <div
                        className="av2-tour__bk"
                        style={{
                            top: rect.bottom + HALO_PAD,
                            left: 0,
                            right: 0,
                            bottom: 0,
                        }}
                        onClick={onClose}
                    />
                    <div
                        className="av2-tour__halo"
                        style={{
                            top: rect.top - HALO_PAD,
                            left: rect.left - HALO_PAD,
                            width: rect.width + HALO_PAD * 2,
                            height: rect.height + HALO_PAD * 2,
                        }}
                    />
                </>
            ) : (
                // Target not found — render solid backdrop so user can still close
                <div className="av2-tour__bk av2-tour__bk--full" onClick={onClose} />
            )}

            <div
                ref={tooltipRef}
                className="av2-tour__tooltip"
                style={tooltipPos}
                role="document"
            >
                <div className="av2-tour__step-num">
                    <FormattedMessage
                        id="analyticsV2.tour.stepCounter"
                        defaultMessage="{current} of {total}"
                        values={{ current: stepIndex + 1, total: steps.length }}
                    />
                </div>
                <h4 id="av2-tour-title" className="av2-tour__title">
                    <FormattedMessage id={step.titleId} defaultMessage={step.titleDefault} />
                </h4>
                <p className="av2-tour__body">
                    <FormattedMessage id={step.bodyId} defaultMessage={step.bodyDefault} />
                </p>
                <div className="av2-tour__progress" aria-hidden="true">
                    {steps.map((_, i) => (
                        <span
                            key={i}
                            className={`av2-tour__dot${
                                i === stepIndex ? ' av2-tour__dot--active' : ''
                            }${i < stepIndex ? ' av2-tour__dot--done' : ''}`}
                        />
                    ))}
                </div>
                <div className="av2-tour__controls">
                    <button
                        type="button"
                        className="av2-tour__btn av2-tour__btn--skip"
                        onClick={onClose}
                    >
                        <FormattedMessage id="analyticsV2.tour.skip" defaultMessage="Skip" />
                    </button>
                    <div className="av2-tour__nav">
                        {!isFirst && (
                            <button
                                type="button"
                                className="av2-tour__btn av2-tour__btn--back"
                                onClick={goPrev}
                            >
                                <FormattedMessage id="analyticsV2.tour.back" defaultMessage="Back" />
                            </button>
                        )}
                        <button
                            type="button"
                            className="av2-tour__btn av2-tour__btn--next"
                            onClick={goNext}
                            autoFocus
                        >
                            {isLast ? (
                                <FormattedMessage id="analyticsV2.tour.done" defaultMessage="Done" />
                            ) : (
                                <FormattedMessage id="analyticsV2.tour.next" defaultMessage="Next" />
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default GuidedTour;
